import React, { useEffect, useMemo, useState } from 'react';
import { getSessionStorageItem, setSessionStorageItem } from '../../utils/browserStorage';

const SESSION_TYPES = [
  { label: 'Equine', value: 'equine' },
  { label: 'Black Background', value: 'black-background' },
  { label: 'Wedding', value: 'wedding' },
  { label: 'Couples', value: 'couples' },
  { label: 'Family', value: 'family' },
  { label: 'Senior', value: 'senior' },
  { label: 'Prom', value: 'prom' },
  { label: 'Pet', value: 'pets' },
  { label: 'Portrait', value: 'portrait' },
  { label: 'Horse Show Private Client', value: 'horse-show-private-client' }
];

export const HeroSection = ({ scrollY }) => {
  const [selectedSessionType, setSelectedSessionType] = useState('equine');
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotionPreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updateMotionPreference();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateMotionPreference);
      return () => mediaQuery.removeEventListener('change', updateMotionPreference);
    }

    mediaQuery.addListener(updateMotionPreference);
    return () => mediaQuery.removeListener(updateMotionPreference);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const storedSessionType = getSessionStorageItem('hero_selected_session_type');

    if (storedSessionType && SESSION_TYPES.some((session) => session.value === storedSessionType)) {
      setSelectedSessionType(storedSessionType);
    }
  }, []);

  const selectedSession = useMemo(
    () => SESSION_TYPES.find((session) => session.value === selectedSessionType) || SESSION_TYPES[0],
    [selectedSessionType]
  );

  const inquiryUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return `?service_type=${encodeURIComponent(selectedSession.value)}#contact-form`;
    }

    const params = new URLSearchParams(window.location.search);

    ['service', 'service_type', 'session', 'session_type'].forEach((paramName) => {
      params.delete(paramName);
    });

    params.set('service_type', selectedSession.value);

    return `?${params.toString()}#contact-form`;
  }, [selectedSession.value]);

  const handleAvailabilityClick = () => {
    if (typeof window !== 'undefined') {
      setSessionStorageItem('hero_selected_session_type', selectedSession.value);
      setSessionStorageItem('hero_focus_contact_form', '1');
      window.dispatchEvent(new CustomEvent('hero:availability-click'));
    }
  };

  return (
    <section id="home" className="relative min-h-screen scroll-mt-24 flex items-center justify-center overflow-hidden">
      <picture className="absolute inset-0">
        <source srcSet="/images/hero-dauntless.webp" type="image/webp" />
        <img
          src="/images/hero-dauntless.jpg"
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          loading="eager"
          decoding="async"
          width="3240"
          height="2160"
          sizes="100vw"
          className="h-full w-full object-cover object-center"
          style={{
            transform: prefersReducedMotion ? 'none' : `translateY(${scrollY * 0.5}px)`
          }}
        />
      </picture>

      <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-teal-900/40" />

      <div className="relative z-10 text-center px-4 text-white max-w-5xl mx-auto">
        <h1 className="text-4xl md:text-7xl font-bold mb-3 md:mb-4">Cassidy Sullivan Photography</h1>

        <p className="text-lg md:text-2xl mb-3 md:mb-4">Equine • Wedding • Portrait Photography</p>

        <p className="hidden md:block text-lg md:text-xl mb-8 max-w-3xl mx-auto leading-relaxed">
          Specializing in capturing the beauty of horses and the precious moments of life&apos;s most important
          celebrations.
        </p>

        <div className="mb-4 md:mb-5">
          <p className="text-xs md:text-base uppercase tracking-wide font-semibold text-white/90 mb-2 md:mb-3">
            What are you planning?
          </p>

          <div className="relative mx-auto w-full max-w-sm md:max-w-md">
            <label htmlFor="hero-session-type" className="sr-only">Select session type</label>
            <select
              id="hero-session-type"
              value={selectedSession.value}
              onChange={(event) => {
                const value = event.target.value;
                setSelectedSessionType(value);
                if (typeof window !== 'undefined') {
                  setSessionStorageItem('hero_selected_session_type', value);
                }
              }}
              data-event="home_selector_click"
              data-session-type={selectedSession.value}
              className="w-full appearance-none rounded-full border border-cyan-200/70 bg-white/90 px-5 py-3 pr-12 text-slate-900 shadow-lg shadow-black/20 backdrop-blur-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
            >
              {SESSION_TYPES.map((session) => (
                <option key={session.value} value={session.value} className="text-slate-900">
                  {session.label}
                </option>
              ))}
            </select>
            <svg
              aria-hidden="true"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-cyan-700"
            >
              <path d="M6 8l4 4 4-4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

        </div>

        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center mb-4 md:mb-5">
          <a
            href="#portfolio"
            className="hidden md:inline-block bg-gradient-to-r from-teal-500 to-cyan-600 text-white px-8 py-3 rounded-full font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
          >
            View My Work
          </a>

          <a
            href={inquiryUrl}
            onClick={handleAvailabilityClick}
            data-event="home_selector_cta_click"
            data-session-type={selectedSession.value}
            aria-label={`Check ${selectedSession.label} availability in contact form`}
            className="inline-block bg-white/20 backdrop-blur-sm text-white px-8 py-3 rounded-full font-semibold hover:bg-white/30 transition-colors border border-white/40 hover:border-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
          >
            Check {selectedSession.label} Availability
          </a>

          <a
            href="tel:+16069226307"
            data-event="home_call_cta_click"
            aria-label="Call or text Cassidy Sullivan Photography"
            className="inline-block rounded-full border border-white/40 bg-slate-900/40 px-8 py-3 font-semibold text-white transition-colors hover:bg-slate-900/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
          >
            Call / Text (606) 922-6307
          </a>
        </div>

        <p className="text-sm md:text-base text-white/90 font-medium">
          Lexington, KY • Equine-first expertise • Fully guided sessions
        </p>
      </div>
    </section>
  );
};
